import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, Loader2, GraduationCap } from "lucide-react";
import ReactMarkdown from "react-markdown";

export default function GuruAI() {
  const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
  const MODEL_NAME = "gemini-flash-latest";

  const [messages, setMessages] = useState([
    {
      role: "model",
      text: "Halo adik-adik! 👋 Saya **Miss Nami**.\n\nSaya bisa membantumu belajar:\n1. 🇬🇧 **Bahasa Inggris**\n2. 🧮 **Matematika**\n3. 🚀 **Fisika Dasar**\n\nMau belajar apa hari ini?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  // Auto-scroll ke pesan terbaru
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async function handleSend() {
    if (!input.trim() || loading) return;
    if (!API_KEY) {
      alert("⚠️ API Key belum dipasang.");
      return;
    }

    const userMessage = input;
    setInput("");

    // Update UI User
    const newHistory = [...messages, { role: "user", text: userMessage }];
    setMessages(newHistory);
    setLoading(true);

    try {
      const apiHistory = newHistory.slice(1).map((msg) => ({
        role: msg.role === "model" ? "model" : "user",
        parts: [{ text: msg.text }],
      }));

      // --- INSTRUKSI SISTEM ---
      const systemInstruction = `
        You are Miss Nami, a cheerful and patient Tutor for elementary students.
        Your expertise is in **English**, **Mathematics**, and **Basic Physics**.
        
        RULES:
        1. **LANGUAGE**: Explain primarily in **INDONESIAN** (90%). Use English only when teaching English vocabulary.
        2. **TONE**: Friendly, encouraging, simple, and fun (like a Kindergarten/Primary school teacher). Use emojis (🍎, 🌟, ✅, 🚀).
        3. **SUBJECTS**:
           - **ENGLISH**: Focus on vocabulary, simple sentences, and pronunciation tips.
           - **MATH**: Focus on arithmetic (add, subtract, multiply, divide), simple logic, and word problems. Show step-by-step calculation.
           - **PHYSICS**: Focus on natural phenomena (why sky is blue, gravity, magnets) explained simply without complex formulas unless asked.
        4. **FORMATTING**:
           - **Bold** key terms (English words, Math answers, Physics terms).
           - Use lists for steps.
        5. **REFUSAL**: If asked about topics outside these 3 (e.g., politics, coding, gossip), politely say: "Maaf, Miss Nami hanya bisa bantu belajar Bahasa Inggris, Matematika, dan Fisika ya! 😊"
        
        Example (Math):
        User: "2 dikali 5 berapa?"
        Nami: "Yuk kita hitung! 🖐️\n2 dikali 5 artinya angka 2-nya ada 5 kali.\n2 + 2 + 2 + 2 + 2 = **10**.\nJadi jawabannya adalah **10**! 🌟"

        Example (Physics):
        User: "Kenapa benda jatuh ke bawah?"
        Nami: "Itu karena adanya **Gaya Gravitasi**! 🍏\nBumi kita punya magnet raksasa yang menarik semua benda ke tanah, supaya kita tidak melayang ke angkasa. Keren kan? 🚀"
      `;

      const payload = {
        contents: apiHistory,
        system_instruction: { parts: [{ text: systemInstruction }] },
        generationConfig: {
          temperature: 0.7,
          maxOutputTokens: 2500,
        },
      };

      let attempt = 0;
      let success = false;
      let replyText = "";

      // Retry Logic
      while (attempt < 3 && !success) {
        try {
          const response = await fetch(
            `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_NAME}:generateContent?key=${API_KEY}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            }
          );
          const data = await response.json();

          if (!response.ok) {
            if (response.status === 429) {
              await delay(2000);
              attempt++;
              continue;
            }
            throw new Error(data.error?.message || "Koneksi Bermasalah");
          }

          if (data.candidates?.[0]?.content) {
            replyText = data.candidates[0].content.parts[0].text;
            success = true;
          } else {
            replyText =
              "Maaf, Miss Nami sedang berpikir. Coba tanya sekali lagi ya? 🤔";
            success = true;
          }
        } catch (err) {
          attempt++;
          if (attempt >= 3) throw err;
          await delay(1500);
        }
      }

      setMessages((prev) => [...prev, { role: "model", text: replyText }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "model", text: `❌ Ada masalah: ${error.message}` },
      ]);
    } finally {
      setLoading(false);
    }
  }

  // --- STYLES (Tema Hijau Emerald) ---
  const styles = {
    wrapper: {
      display: "flex",
      justifyContent: "center",
      background: "#F1F5F9",
      minHeight: "100vh",
      fontFamily: "'Segoe UI', sans-serif",
    },
    mobileContainer: {
      width: "100%",
      maxWidth: "480px",
      background: "#FFFFFF",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 0 20px rgba(0,0,0,0.05)",
      height: "100vh",
      // PERBAIKAN: Menambahkan padding bawah agar input tidak tertutup navigasi
      paddingBottom: "90px",
      boxSizing: "border-box",
    },
    header: {
      padding: "16px 20px",
      background: "rgba(255,255,255,0.95)",
      backdropFilter: "blur(5px)",
      borderBottom: "1px solid #E2E8F0",
      display: "flex",
      alignItems: "center",
      gap: 15,
      position: "sticky",
      top: 0,
      zIndex: 10,
    },
    avatar: {
      width: 45,
      height: 45,
      background: "linear-gradient(135deg, #10B981, #059669)",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
    },
    chatArea: {
      flex: 1,
      padding: "20px",
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      gap: "18px",
      background: "#F8FAFC",
    },
    bubbleUser: {
      alignSelf: "flex-end",
      background: "#059669",
      color: "white",
      padding: "10px 18px",
      borderRadius: "18px 18px 2px 18px",
      maxWidth: "85%",
      fontSize: "14.5px",
      boxShadow: "0 2px 4px rgba(5, 150, 105, 0.1)",
    },
    bubbleModel: {
      alignSelf: "flex-start",
      background: "white",
      border: "1px solid #E2E8F0",
      color: "#334155",
      padding: "10px 18px",
      borderRadius: "18px 18px 18px 2px",
      maxWidth: "85%",
      fontSize: "14.5px",
      lineHeight: "1.6",
      boxShadow: "0 2px 4px rgba(0,0,0,0.03)",
    },
    inputArea: {
      padding: "15px 20px",
      background: "white",
      borderTop: "1px solid #E2E8F0",
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    input: {
      width: "100%",
      padding: "14px 20px",
      borderRadius: "25px",
      border: "1px solid #CBD5E1",
      background: "#F1F5F9",
      fontSize: "14px",
      outline: "none",
    },
    sendBtn: {
      width: 48,
      height: 48,
      borderRadius: "50%",
      background: "#059669",
      color: "white",
      border: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    },
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.mobileContainer}>
        {/* Header */}
        <div style={styles.header}>
          <Link to="/" style={{ color: "#64748B" }}>
            <ArrowLeft size={22} />
          </Link>
          <div style={styles.avatar}>
            <GraduationCap size={24} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: "16px", color: "#0F172A" }}>
              Miss Nami
            </h3>
            <p style={{ margin: 0, fontSize: "12px", color: "#10B981" }}>
              ● Inggris • Mate • Fisika
            </p>
          </div>
        </div>

        {/* Chat Area */}
        <div style={styles.chatArea}>
          {messages.map((m, i) => (
            <div
              key={i}
              style={m.role === "user" ? styles.bubbleUser : styles.bubbleModel}
            >
              {m.role === "user" ? (
                <div>{m.text}</div>
              ) : (
                <div className="markdown-content">
                  <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => (
                        <p style={{ margin: "0 0 8px 0" }} {...props} />
                      ),
                      ul: ({ node, ...props }) => (
                        <ul
                          style={{ margin: "0 0 8px 0", paddingLeft: "20px" }}
                          {...props}
                        />
                      ),
                      li: ({ node, ...props }) => (
                        <li style={{ marginBottom: "4px" }} {...props} />
                      ),
                      strong: ({ node, ...props }) => (
                        <strong
                          style={{ fontWeight: "700", color: "#059669" }}
                          {...props}
                        />
                      ),
                    }}
                  >
                    {m.text}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div
              style={{
                alignSelf: "flex-start",
                color: "#94A3B8",
                fontSize: "12px",
                display: "flex",
                gap: 6,
                marginLeft: 20,
              }}
            >
              <Loader2 size={16} className="animate-spin" /> Miss Nami sedang
              menulis...
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={styles.inputArea}>
          <input
            style={styles.input}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder="Tanya pelajaran..."
            disabled={loading}
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            style={styles.sendBtn}
          >
            <Send size={22} />
          </button>
        </div>
      </div>
    </div>
  );
}
